import type { MemberClient } from "@features/api/memberApi";
import type { AccountReadsClient } from "@features/api/AccountReadsApi";
import type {
  ChecklistResponseDTO,
  ChecklistStepItem,
  ChecklistStepKey,
} from "../types";
import log from "logbeacon";

export const CHECKLIST_API_TIMEOUT = 3000; // 3 秒超时门禁 — PRD §3

/**
 * 模块级 API client 注入口（store 层不在 React 树内，拿不到 Context）。
 * 由 OspApiClientProvider 每次渲染时注册（auth 变化 → client 重建 → 自动更新），
 * 与 subscribeEmailStatusResolver 同模式。
 */
type ChecklistApiClient = AccountReadsClient<any>;
type MemberApiClient = MemberClient<any>;

let checklistApiClient: ChecklistApiClient | undefined;
let memberApiClient: MemberApiClient | undefined;

export function setChecklistApiClients(clients: {
  checklistApi?: ChecklistApiClient;
  memberApi?: MemberApiClient;
}): void {
  checklistApiClient = clients.checklistApi;
  memberApiClient = clients.memberApi;
}

/**
 * 将后端返回的原始数据（支持 OpenAPI snake_case 与前端 camelCase）归一化为 ChecklistResponseDTO
 */
export function normalizeChecklistResponse(raw: any): ChecklistResponseDTO {
  if (!raw || typeof raw !== "object") {
    return {
      totalCount: 6,
      completedCount: 0,
      allCompleted: false,
      firstIncompleteStepId: 1,
      showCelebration: false,
      ffScore: null,
      steps: [],
    };
  }

  const rawSteps = Array.isArray(raw.steps) ? raw.steps : [];
  const steps: ChecklistStepItem[] = rawSteps.map((s: any) => ({
    stepId: s.stepId ?? s.step_id ?? 0,
    stepKey: (s.stepKey ?? s.step_key ?? "") as ChecklistStepKey,
    completed: Boolean(s.completed),
    completedAt: s.completedAt ?? s.completed_at ?? null,
  }));

  return {
    totalCount: raw.totalCount ?? raw.total_count ?? 6,
    completedCount: raw.completedCount ?? raw.completed_count ?? 0,
    allCompleted: Boolean(raw.allCompleted ?? raw.all_completed),
    firstIncompleteStepId:
      raw.firstIncompleteStepId !== undefined
        ? raw.firstIncompleteStepId
        : raw.first_incomplete_step_id !== undefined
          ? raw.first_incomplete_step_id
          : null,
    showCelebration: Boolean(raw.showCelebration ?? raw.show_celebration),
    ffScore:
      raw.ffScore !== undefined
        ? raw.ffScore
        : raw.ff_score !== undefined
          ? raw.ff_score
          : null,
    steps,
  };
}

/**
 * 等待 checklist client 注册（最长 2s）。
 * 首帧时序：子组件 effect 先于 OspApiClientProvider 的 client 创建 effect 执行，
 * 首次 fetch 可能早于注册 —— 短轮询兜底而不是直接失败。
 */
async function waitForChecklistClient(
  deadlineMs = 2000,
): Promise<ChecklistApiClient | undefined> {
  const start = Date.now();
  while (!checklistApiClient && Date.now() - start < deadlineMs) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  return checklistApiClient;
}

/**
 * 获取起步清单聚合状态
 * GET {ospIndexBaseUrl}/api/v1/onboarding/checklist（Index 网关；后端实际部署位置，
 * tracker 网关 /v2/tracker/api/v1/... 该路由 404，预检失败在浏览器表现为 CORS 报错）
 * 走 AccountReadsApiClient：自动带 JWT / OS-Device-Id / OS-Device-Type / OS-App-Id /
 * OS-Chain-Id / OS-Reg-Brand 头（与后端联调验证过的头集合一致）
 * 3 秒超时自动 abort — fe-spec §1.3 第 4 条
 * 6 项（含 notification）在 web 与原生壳全量展示、直接采用后端口径；
 * notification 仅能由 app 端授权完成 —— 用户在 app 打开通知后自动同步。
 */
export async function getChecklistStatus(): Promise<ChecklistResponseDTO> {
  const client = await waitForChecklistClient();
  if (!client) {
    throw new Error("checklist api client not ready");
  }
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CHECKLIST_API_TIMEOUT);
  try {
    const res = await client.api.getChecklist({
      signal: controller.signal,
    });
    if (res.error) {
      throw new Error(`HTTP ${res.status}`);
    }
    return normalizeChecklistResponse(res.data);
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * 庆祝弹窗已读防重
 * POST /agents/preferences { checklist_celebration_dismissed: true }
 * 复用现有 mixture 偏好接口 — be-design §4.1.2
 * 静默降级：本地已有 localStorage 双重防重，服务端失败不阻塞关闭弹窗；
 * 返回是否已落库，调用方据此在失败时记待补发标记（换设备防重依赖服务端）
 */
export async function dismissCelebrationModal(): Promise<boolean> {
  try {
    await memberApiClient?.agents.addPreference({
      checklist_celebration_dismissed: true,
    });
    return true;
  } catch (err) {
    log.warn("[setup-checklist] dismiss celebration failed:", err);
    return false;
  }
}
